/**
 * Los paneles de la consola de partido: lo que se MIRA.
 *
 * Regla de diseño de todo este archivo, y es una sola: **se lee de reojo, a un
 * metro, con luz de estadio.** El DT no está sentado frente a una planilla; está
 * mirando la cancha y baja la vista dos segundos. De ahí sale todo lo demás:
 *
 *  - **Nada de tablas densas.** El portal tiene tablas porque un dirigente las
 *    lee en una laptop; acá el mismo dato va en fichas grandes.
 *  - **Números tabulares y enormes.** El marcador es lo único que crece hasta
 *    llenar el ancho; el resto se subordina.
 *  - **El color no es la única señal.** El que saca lleva un punto Y una
 *    etiqueta; el aviso de sustituciones lleva marco Y texto. Un tercio de las
 *    canchas del interior tienen luz amarilla y hay daltónicos en los bancos.
 *  - **Blancos honestos.** Sin formación cargada, la cancha muestra las
 *    posiciones vacías con su número de posición, como el acta en papel: no
 *    inventa una rotación.
 */
import type { MatchReport, TeamSide } from '@scout/core';
import { useT } from '../../i18n/use-t';
import type { TFunction } from '../../i18n/index';
import { Court, playersFromRotation } from '../../components/Court';
import {
  ageSeconds,
  pairedWith,
  timecode,
  type BenchSnapshot,
  type BenchTeamMeta,
  type BenchTeamState,
  type LinkState,
} from '../../lib/bench';

const SIDES: readonly TeamSide[] = ['home', 'away'];

// ---------------------------------------------------------------------------
// El cartel del enlace: la promesa de §5.3 hecha pantalla
// ---------------------------------------------------------------------------

/**
 * Cuando se cae la red, EL PARTIDO SIGUE.
 *
 * Lo que este cartel hace —y lo que no— es toda la diferencia:
 *  - **no** borra el marcador ni tapa la pantalla: lo último que se sabe sigue
 *    ahí, porque es lo mejor que hay y el DT lo necesita igual;
 *  - **sí** dice la hora del último dato, porque un marcador congelado sin
 *    aviso es peor que no tener nada;
 *  - **sí** recuerda cuál es el pedido oficial cuando no hay tablet: la seña.
 *    No es una disculpa, es el reglamento (regla 15.5.1; VCS art. 21 para el
 *    challenge, que contempla explícitamente que la ETT falle).
 */
export function LinkBanner({
  link,
  lastSyncAt,
  now,
  onRetry,
}: {
  link: LinkState;
  lastSyncAt: number | null;
  now: number;
  onRetry: () => void;
}): JSX.Element | null {
  const t = useT();
  if (link === 'LIVE') return null;

  const seconds = lastSyncAt === null ? null : Math.max(0, Math.floor((now - lastSyncAt) / 1000));
  const tone = link === 'STALE' || link === 'COLD' ? 'bad' : 'warn';

  return (
    <div className={`bench-banner ${tone}`} role="status">
      <div className="bench-banner-head">
        {link === 'COLD' ? t('bench.link.cold') : t('bench.link.offline')}
      </div>
      <div className="bench-banner-body">
        {t('bench.link.keepPlaying')}
        {seconds !== null ? ` · ${t('bench.link.lastData', { seconds })}` : ''}
      </div>
      <button type="button" className="bench-btn ghost" onClick={onRetry}>
        {t('bench.link.retry')}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Marcador
// ---------------------------------------------------------------------------

/**
 * El marcador. `huge` lo lleva al tamaño de la pantalla de estadio, que es una
 * VISTA de esta misma pieza y no un componente aparte: si fueran dos, un día
 * dirían números distintos.
 */
export function ScoreHero({
  snapshot,
  huge = false,
}: {
  snapshot: BenchSnapshot;
  huge?: boolean;
}): JSX.Element {
  const t = useT();
  return (
    <div className={huge ? 'bench-score huge' : 'bench-score'}>
      {SIDES.map((side) => (
        <div key={side} className={`bench-side ${side}`}>
          <div className="bench-team-name">
            {snapshot.meta[side].name}
            {snapshot.servingTeam === side ? (
              <span className="bench-serving" title={t('bench.score.serving')}>
                <span className="dot" /> {t('bench.score.servingShort')}
              </span>
            ) : null}
          </div>
          <div className="bench-points">{snapshot.teams[side].score}</div>
          <div className="bench-sets">{snapshot.setsWon[side]}</div>
        </div>
      ))}
      <div className="bench-middle">
        <div className="bench-set-label">{t('common.set', { set: snapshot.currentSet })}</div>
        {snapshot.finishedSets.length > 0 ? (
          <div className="bench-set-pills">
            {snapshot.finishedSets.map((s) => (
              <span key={s.setNumber} className="bench-pill">
                {s.home}–{s.away}
              </span>
            ))}
          </div>
        ) : null}
        {snapshot.sheetClosed ? <div className="bench-closed">{t('bench.score.closed')}</div> : null}
      </div>
    </div>
  );
}

/** Cupos del set: tiempos, sustituciones y challenges, en fichas grandes. */
export function TeamChips({
  team,
  state,
  name,
}: {
  team: TeamSide;
  state: BenchTeamState;
  name: string;
}): JSX.Element {
  const t = useT();
  const subs = state.substitutions;
  return (
    <div className={`bench-chips ${team}`}>
      <span className="bench-chip-team">{name}</span>
      <span className="bench-chip">
        <b>{state.timeoutsLimit - state.timeoutsUsed}</b> {t('bench.score.timeoutsLeft')}
      </span>
      <span className={`bench-chip${subs.warn ? ' warn' : ''}${subs.blocked ? ' bad' : ''}`}>
        <b>{subs.remaining}</b> {t('bench.score.subsLeft')}
      </span>
      <span className="bench-chip">
        <b>{state.challengesRemaining}</b> {t('bench.score.challengesLeft')}
      </span>
      {state.exceptionalSubsUsed > 0 ? (
        <span className="bench-chip muted">
          {t('bench.score.exceptional', { count: state.exceptionalSubsUsed })}
        </span>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// La cancha
// ---------------------------------------------------------------------------

/**
 * La rotación en cancha, girable.
 *
 * `flipped` gira 180°, que es lo que hace falta cuando el cuerpo técnico está
 * sentado del otro lado. Sin eso, el DT tiene que hacer la transformación en la
 * cabeza mientras mira el partido — que es el motivo por el que el bench del
 * competidor la tiene, y es gratis.
 *
 * `highlight` pinta un número y su **pareja de la regla 15.6**: cuando el banco
 * elige a quién sacar, la pantalla muestra con quién quedó atada esa casilla.
 */
export function CourtPanel({
  team,
  state,
  name,
  flipped,
  onFlip,
  highlight = null,
  meta,
  serving = false,
}: {
  team: TeamSide;
  state: BenchTeamState;
  name: string;
  flipped: boolean;
  onFlip?: () => void;
  highlight?: number | null;
  /** Plantel: rol de cada ficha y líberos marcados. */
  meta?: BenchTeamMeta;
  /** Este equipo saca: la pelota se dibuja junto a P1. */
  serving?: boolean;
}): JSX.Element {
  const t = useT();
  const pareja = highlight === null ? null : pairedWith(state.slots, highlight);
  const sinFormacion = state.rotation.length === 0;
  const players = playersFromRotation(state.rotation, meta?.players ?? [], meta?.liberos ?? []).map(
    (player) => {
      if (player === null) return null;
      if (highlight !== null && player.number === highlight) return { ...player, mark: 'picked' as const };
      if (pareja !== null && player.number === pareja) return { ...player, mark: 'paired' as const };
      return player;
    },
  );

  return (
    <section className={`bench-card court ${team}`}>
      <header className="bench-card-head">
        <h2>{t('bench.court.title', { team: name })}</h2>
        {onFlip ? (
          <button type="button" className="bench-btn ghost" onClick={onFlip} aria-pressed={flipped}>
            {flipped ? t('bench.court.unflip') : t('bench.court.flip')}
          </button>
        ) : null}
      </header>
      {/* La red va dibujada (y rotulada) dentro de la cancha: sin ella,
          "arriba" y "abajo" no significan nada cuando está girada. Sin
          formación, las posiciones quedan vacías con su número, como el acta. */}
      <div className="bench-court">
        <Court
          label={t('bench.court.title', { team: name })}
          sides={[{ team, players, serving, zonePrefix: 'P' }]}
          rotated={flipped}
          netLabel={t('bench.court.net')}
        />
      </div>
      {sinFormacion ? <p className="bench-empty">{t('bench.court.noLineup')}</p> : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Rallies
// ---------------------------------------------------------------------------

/**
 * La lista de rallies con salto al saque.
 *
 * `onJump` sólo aparece si hay VAR configurado en ESTE navegador (el clip sale
 * por LAN, ADR-017). Sin VAR la lista igual sirve: es el recorrido del set con
 * el marcador punto por punto, que es lo que el DT repasa en el tiempo.
 */
export function RalliesPanel({
  snapshot,
  onJump = null,
  limit = 30,
}: {
  snapshot: BenchSnapshot;
  onJump?: ((videoNs: number) => void) | null;
  limit?: number;
}): JSX.Element {
  const t = useT();
  const delSet = snapshot.rallies.filter((r) => r.set === snapshot.currentSet);
  const visibles = delSet.slice(-limit).reverse();

  return (
    <section className="bench-card">
      <header className="bench-card-head">
        <h2>{t('bench.rallies.title', { set: snapshot.currentSet })}</h2>
      </header>
      {visibles.length === 0 ? (
        <p className="bench-empty">{t('bench.rallies.empty')}</p>
      ) : (
        <ul className="bench-rallies">
          {visibles.map((rally) => (
            <li key={`${rally.set}-${rally.index}`} className={`bench-rally ${rally.winner ?? ''}`}>
              <span className="bench-rally-score">
                {rally.home}–{rally.away}
              </span>
              <span className="bench-rally-meta">
                {rally.server !== null
                  ? t('bench.rallies.servedBy', { team: snapshot.meta[rally.server].name })
                  : t('bench.rallies.unknownServer')}
              </span>
              {rally.startedAtNs !== null && onJump ? (
                <button
                  type="button"
                  className="bench-btn small"
                  onClick={() => onJump(rally.startedAtNs as number)}
                >
                  {t('bench.rallies.jump', { tc: timecode(rally.startedAtNs) })}
                </button>
              ) : (
                <span className="bench-rally-tc">
                  {rally.startedAtNs !== null
                    ? timecode(rally.startedAtNs)
                    : t('bench.rallies.noTimecode')}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Los números del partido en curso
// ---------------------------------------------------------------------------

function pct(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function attackEfficiency(report: MatchReport, side: TeamSide): number | null {
  const line = report[side].skills['attack'];
  return line === undefined || line.total === 0 ? null : line.efficiency;
}

function receptionPositive(report: MatchReport, side: TeamSide): number | null {
  const line = report[side].skills['reception'];
  if (line === undefined || line.total === 0) return null;
  const buenas = (line.byEvaluation['#'] ?? 0) + (line.byEvaluation['+'] ?? 0);
  return buenas / line.total;
}

/**
 * Los cuatro números que el DT mira en el tiempo muerto.
 *
 * Cuatro y no diez: la pantalla se lee de reojo. Están elegidos por lo que
 * cambia una decisión en el banco — el sideout dice si el equipo sostiene su
 * saque rival, el break si lastima con el suyo, el ataque si vale seguir
 * insistiendo y la recepción si hay que tapar a alguien. El resto está en el
 * reporte completo, que se abre después del partido.
 */
export function NumbersPanel({
  report,
  unavailable,
  meta,
}: {
  report: MatchReport | null;
  unavailable: boolean;
  meta: BenchSnapshot['meta'];
}): JSX.Element {
  const t = useT();
  if (report === null) {
    return (
      <section className="bench-card">
        <header className="bench-card-head">
          <h2>{t('bench.numbers.title')}</h2>
        </header>
        <p className="bench-empty">
          {unavailable ? t('bench.numbers.unavailable') : t('bench.numbers.loading')}
        </p>
      </section>
    );
  }

  return (
    <section className="bench-card">
      <header className="bench-card-head">
        <h2>{t('bench.numbers.title')}</h2>
      </header>
      <div className="bench-tiles">
        {SIDES.map((side) => {
          const ataque = attackEfficiency(report, side);
          const recepcion = receptionPositive(report, side);
          return (
            <div key={side} className={`bench-tile-group ${side}`}>
              <div className="bench-tile-team">{meta[side].name}</div>
              <div className="bench-tile">
                <span className="bench-tile-value">{pct(report[side].sideoutPct)}</span>
                <span className="bench-tile-label">{t('bench.numbers.sideout')}</span>
              </div>
              <div className="bench-tile">
                <span className="bench-tile-value">{pct(report[side].breakPct)}</span>
                <span className="bench-tile-label">{t('bench.numbers.break')}</span>
              </div>
              <div className="bench-tile">
                <span className="bench-tile-value">{ataque === null ? '—' : pct(ataque)}</span>
                <span className="bench-tile-label">{t('bench.numbers.attack')}</span>
              </div>
              <div className="bench-tile">
                <span className="bench-tile-value">
                  {recepcion === null ? '—' : pct(recepcion)}
                </span>
                <span className="bench-tile-label">{t('bench.numbers.reception')}</span>
              </div>
            </div>
          );
        })}
      </div>
      <TopScorers report={report} meta={meta} t={t} />
    </section>
  );
}

function TopScorers({
  report,
  meta,
  t,
}: {
  report: MatchReport;
  meta: BenchSnapshot['meta'];
  t: TFunction;
}): JSX.Element | null {
  const todos = SIDES.flatMap((side) =>
    report[side].players.map((p) => ({ ...p, side, team: meta[side].name })),
  )
    .filter((p) => p.pointsScored > 0)
    .sort((a, b) => b.pointsScored - a.pointsScored)
    .slice(0, 5);
  if (todos.length === 0) return null;

  return (
    <div className="bench-scorers">
      <span className="bench-scorers-label">{t('bench.numbers.topScorers')}</span>
      {todos.map((p) => (
        <span key={`${p.side}-${p.number}`} className={`bench-scorer ${p.side}`}>
          <b>{p.pointsScored}</b> #{p.number}
          {p.name !== undefined ? ` ${p.name}` : ''}
        </span>
      ))}
    </div>
  );
}

/** "hace 12 s" — la antigüedad de un pedido, que es lo que urge en la mesa. */
export function Age({ iso, now }: { iso: string; now: number }): JSX.Element {
  const t = useT();
  return <span className="bench-age">{t('bench.queue.ago', { seconds: ageSeconds(iso, now) })}</span>;
}
